<!--
// app/pages/[region]/Tables.vue
-->
<script setup lang="ts">
import type { Phase } from "~/src/season-cluster/domain/models/02.Phase";
import type { StandingPlayoffColors } from "~/src/season-cluster/domain/models/01.Season";
import { useRegions } from "~/app/composables/regions";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useSeasons } from "~/app/composables/season/seasons";
import { useWebContext } from "~/app/composables/webContext";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import CurrentSeasonSliderMatches from "~/app/components/Shared/slider-matches/CurrentSeasonSliderMatches.vue";
import GridPartnersCompetition from "~/app/components/Shared/GridPartnersCompetition.vue";
import HeaderBannerTitle from "~/app/components/Shared/HeaderBannerTitle.vue";
import BracketTablesWorldCup from "~/app/components/pages/region-world-cup/BracketTablesWorldCup.vue";
import SliderArrowsBehaviour from "~/app/components/Shared/slider-matches/SliderArrowsBehaviour.vue";
import BracketTablesNations from "~/app/components/pages/region-nations/BracketTablesNations.vue";
import KwcTablesAllLeagues from "~/app/components/pages/region-world-cup/KwcTablesAllLeagues.vue";
import GermanyBracket from "~/app/components/pages/region-home/GermanyBracket.vue";
import TableStandingRegularPhase from "~/app/components/pages/region-tables/TableStandingRegularPhase.vue";
import GridPlayoffPhase from "~/app/components/pages/region-tables/GridPlayoffPhase.vue";

const localePath = useLocalePath();

const { t } = useI18n();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { isWorldCup, isNations, isSpain, isAmericas, isGermany } = useRegions();
const { region, currentSeason } = useCompetitionStore();
const { getSeasonStanding } = useSeasons();
const { contextSiteTitle, isKings, isQueens } = useWebContext();

const standingPhase = ref<Phase>();
const playoffPhase = ref<Phase>();
const loading = ref<boolean>(false);
const positionLegend = ref<StandingPlayoffColors[]>();
const secondaryPositionLegend = ref<StandingPlayoffColors[]>();
const winnerTeam = ref<number | null>();

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/tables",
    es: "/[region]/clasificacion",
    de: "/[region]/klassifikation",
    fr: "/[region]/classement",
    it: "/[region]/classifica",
    pt: "/[region]/classificacao",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

async function updateStandingPhase(): Promise<Phase | void> {
  try {
    loading.value = true;
    const _seasonId = seasonId.value;
    if (!_seasonId) return;
    const {
      phase,
      playoff,
      winnerTeamId,
      positionLegend: legend,
      secondaryPositionLegend: secondaryLegend,
    } = await getSeasonStanding(_seasonId);
    standingPhase.value = phase;
    positionLegend.value = legend;
    secondaryPositionLegend.value = secondaryLegend;
    playoffPhase.value = playoff;
    winnerTeam.value = winnerTeamId;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}

if (seasonId.value) {
  await updateStandingPhase();
}

useSeoMeta({
  title: t("header.navCompetition.tables"),
  ogTitle: t("header.navCompetition.tables"),
  description: t("meta.description.tables.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.tables.page", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <header-banner-title :title="$t('tables.banner.title')"></header-banner-title>

  <section v-if="!isWorldCup && !isNations && !isGermany" class="kql-w-container px-4">
    <div class="flex flex-col bigsm:flex-row items-center justify-start gap-2 bigsm:justify-start">
      <nuxt-link
        v-if="seasonClusterId === currentSeason.seasonClusterId && isKings && (isSpain || isAmericas)"
        :to="localePath({ name: 'region-Tables', params: { region: 'world-cup' } })"
        class="btn btn-primary w-full bigsm:w-auto text-center text-sm min-w-max"
        external
        >Road to World Cup</nuxt-link
      >
      <nuxt-link
        v-else-if="seasonClusterId === currentSeason.seasonClusterId && isQueens"
        :to="localePath({ name: 'FinalissimaQueens' })"
        class="btn btn-primary w-full bigsm:w-auto text-center text-sm min-w-max"
        external
        >Road to Finalissima</nuxt-link
      >
      <div class="w-full bigsm:ml-auto">
        <seasons-filter
          v-model:season-id="seasonId"
          v-model:season-cluster-id="seasonClusterId"
          @change="updateStandingPhase"
        />
      </div>
    </div>

    <table-standing-regular-phase
      :season-id="seasonId"
      :loading="loading"
      :position-legend="positionLegend"
      :secondary-position-legend="secondaryPositionLegend"
      :standing-phase="standingPhase"
    ></table-standing-regular-phase>

    <grid-playoff-phase
      :playoff-phase="playoffPhase"
      :season-id="seasonId"
      :winner-team-id="winnerTeam"
    ></grid-playoff-phase>
  </section>

  <section v-if="isWorldCup" class="kql-w-container px-4">
    <bracket-tables-world-cup></bracket-tables-world-cup>
  </section>

  <section v-else-if="isNations" class="kql-w-container px-4">
    <div class="text-right">
      <seasons-filter
        v-model:season-id="seasonId"
        v-model:season-cluster-id="seasonClusterId"
        @change="updateStandingPhase"
      />
    </div>

    <slider-arrows-behaviour
      button-right-id="bracketButtonRight"
      button-left-id="bracketButtonLeft"
      :button-scroll-distance="204"
      slider-container-id="tablesSlider"
      :conditionally-render-if="isNations"
    >
      <bracket-tables-nations></bracket-tables-nations>
    </slider-arrows-behaviour>
  </section>

  <section v-else-if="isGermany" class="kql-w-container">
    <div class="text-right px-4">
      <seasons-filter v-model:season-id="seasonId" v-model:season-cluster-id="seasonClusterId" />
    </div>

    <germany-bracket></germany-bracket>
  </section>

  <section v-if="!isWorldCup" class="py-8">
    <current-season-slider-matches
      section-title
      with-add-to-calendar-in-section
    ></current-season-slider-matches>
  </section>

  <grid-partners-competition></grid-partners-competition>
</template>

<style scoped></style>
